import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import EmptyState from '../common/EmptyState';

const RequisitionsMonitor = () => {
  const navigate = useNavigate();
  const [requisitions, setRequisitions] = useState([]);
  const [expandedProject, setExpandedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    activeProjects: 0,
    projectsList: []
  });

  const toggleProject = (projectName) => {
    setExpandedProject(prev => prev === projectName ? null : projectName);
  };

  useEffect(() => {
    const fetchRequisitions = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/requisitions/global');
        setRequisitions(response.data);
      } catch (err) {
        console.warn('⚠️ Fetch global requisitions failed, using mock data for frontend preview.');
        setRequisitions([
          {
            id: 1,
            title: 'Senior React Developer',
            project_name: 'AI Search Engine',
            skillset: 'React, Redux, Typescript, Webpack',
            exp_required: '5-8 Years',
            budget: 1500000,
            count: 3,
            manager_name: 'Aniket Patel',
            status: 'PUBLISHED',
            created_at: '2026-08-01T10:00:00.000Z'
          },
          {
            id: 2,
            title: 'Node.js Backend Engineer',
            project_name: 'AI Search Engine',
            skillset: 'Node.js, Express, SQLite, Docker',
            exp_required: '3-5 Years',
            budget: 1100000,
            count: 2,
            manager_name: 'Aniket Patel',
            status: 'BU_APPROVED',
            created_at: '2026-08-04T14:30:00.000Z'
          },
          {
            id: 3,
            title: 'Lead QA Automation Engineer',
            project_name: 'Virtual Proctoring Service',
            skillset: 'Selenium, Cypress, Javascript, CI/CD',
            exp_required: '8+ Years',
            budget: 2000000,
            count: 1,
            manager_name: 'Rohan Sharma',
            status: 'FILLED',
            created_at: '2026-07-28T09:15:00.000Z'
          },
          {
            id: 4,
            title: 'UI/UX Product Designer',
            project_name: 'Candidate Portal Overhaul',
            skillset: 'Figma, Design Systems, Prototyping',
            exp_required: '4-6 Years',
            budget: 1300000,
            count: 1,
            manager_name: 'Sneha Roy',
            status: 'DRAFT',
            created_at: '2026-08-05T16:45:00.000Z'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    const fetchStats = async () => {
      try {
        const response = await api.get('/api/requisitions/overview-stats');
        setStats(response.data);
      } catch (err) {
        console.warn('⚠️ Fetch overview stats failed, using fallback mocks.');
        setStats({
          totalEmployees: 48,
          activeProjects: 4,
          projectsList: [
            { name: 'AI Search Engine', status: 'Active', size: 12, max: 15, color: '#4f46e5' },
            { name: 'Virtual Proctoring Service', status: 'Active', size: 8, max: 10, color: '#10b981' },
            { name: 'Candidate Portal Overhaul', status: 'Planning', size: 4, max: 8, color: '#f59e0b' },
            { name: 'Core Infrastructure', status: 'Maintenance', size: 18, max: 20, color: '#6366f1' }
          ]
        });
      }
    };

    fetchRequisitions();
    fetchStats();
  }, []);

  const handlePublish = async (id) => {
    try {
      let hrId = null;
      try {
        const rawUser = localStorage.getItem('user');
        if (rawUser) {
          const u = JSON.parse(rawUser);
          hrId = u.id;
        }
      } catch (e) {}
      await api.post(`/api/requisitions/${id}/publish`, { hrId });
      // Reload requisitions list
      const response = await api.get('/api/requisitions/global');
      setRequisitions(response.data);
    } catch (err) {
      console.error('Failed to publish requisition:', err);
      alert('Failed to publish requisition.');
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'DRAFT':
        return { bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.3)', text: 'var(--warning-color)' };
      case 'BU_APPROVED':
        return { bg: 'rgba(59, 130, 246, 0.12)', border: 'rgba(59, 130, 246, 0.3)', text: '#3b82f6' };
      case 'PUBLISHED':
        return { bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.3)', text: 'var(--success-color)' };
      case 'FILLED':
        return { bg: 'rgba(139, 92, 246, 0.12)', border: 'rgba(139, 92, 246, 0.3)', text: '#8b5cf6' };
      default:
        return { bg: 'var(--bg-hover)', border: 'var(--border-color)', text: 'var(--text-secondary)' };
    }
  };

  // Group items by project name
  const groupedRequisitions = requisitions.reduce((acc, req) => {
    const proj = req.project_name || 'General Pool';
    if (!acc[proj]) acc[proj] = [];
    acc[proj].push(req);
    return acc;
  }, {});

  // Metrics
  const totalOpenings = requisitions.length;
  const activePublished = requisitions.filter(r => r.status === 'PUBLISHED').length;
  const pendingApprovals = requisitions.filter(r => r.status === 'DRAFT' || r.status === 'BU_APPROVED').length;
  const filledPositions = requisitions.filter(r => r.status === 'FILLED').length;

  // Group items by manager
  const managers = requisitions.reduce((acc, req) => {
    const manager = req.manager_name || 'Assigned Manager';
    if (!acc[manager]) {
      acc[manager] = {
        name: manager,
        project: req.project_name || 'General Pool',
        openingsCount: 0,
        openings: []
      };
    }
    acc[manager].openingsCount += (parseInt(req.count) || 1);
    acc[manager].openings.push(req);
    return acc;
  }, {});



  return (
    <div style={styles.container}>
      {/* HEADER SECTION */}
      <header style={styles.header}>
        <div>
          <h2 style={styles.title}>Workspace Overview</h2>
          <p style={styles.subtitle}>Track active hiring requisitions, team staffing levels, and project indicators</p>
        </div>
      </header>

      {loading ? (
        <div style={styles.loader}>Loading overview metrics...</div>
      ) : (
        <>
          {/* METRICS CARDS */}
          <section style={styles.metricsGrid}>
            <div style={{ ...styles.metricCard, borderLeft: '4px solid var(--primary-color)' }}>
              <p style={styles.metricLabel}>Total Employees</p>
              <h3 style={{ ...styles.metricValue, color: 'var(--primary-color)' }}>{stats.totalEmployees}</h3>
            </div>
            <div style={{ ...styles.metricCard, borderLeft: '4px solid #6366f1' }}>
              <p style={styles.metricLabel}>Active Projects</p>
              <h3 style={{ ...styles.metricValue, color: '#6366f1' }}>{stats.activeProjects}</h3>
            </div>
            <div style={styles.metricCard}>
              <p style={styles.metricLabel}>Total Requisitions</p>
              <h3 style={styles.metricValue}>{totalOpenings}</h3>
            </div>
            <div style={{ ...styles.metricCard, borderLeft: '4px solid var(--success-color)' }}>
              <p style={styles.metricLabel}>Published Jobs (Active)</p>
              <h3 style={{ ...styles.metricValue, color: 'var(--success-color)' }}>{activePublished}</h3>
            </div>
            <div style={{ ...styles.metricCard, borderLeft: '4px solid #3b82f6' }}>
              <p style={styles.metricLabel}>Pending Positions</p>
              <h3 style={{ ...styles.metricValue, color: '#3b82f6' }}>{pendingApprovals}</h3>
            </div>
            <div style={{ ...styles.metricCard, borderLeft: '4px solid #8b5cf6' }}>
              <p style={styles.metricLabel}>Filled Positions</p>
              <h3 style={{ ...styles.metricValue, color: '#8b5cf6' }}>{filledPositions}</h3>
            </div>
          </section>

          {/* DASHBOARD CONTENT: MANAGERS */}
          <section style={styles.dashboardSplitGrid}>
            {/* Managers Column */}
            <div style={styles.dashboardCard}>
              <h3 style={styles.cardHeaderTitle}>Approved Openings by Manager</h3>
              <div style={styles.managersList}>
                {Object.keys(managers).length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', padding: '16px 0' }}>No openings approved yet.</p>
                ) : (
                  Object.values(managers).map((mgr) => (
                    <div key={mgr.name} style={styles.managerItem}>
                      <div style={styles.managerAvatar}>
                        {mgr.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </div>
                      <div style={styles.managerInfo}>
                        <h4 style={styles.managerName}>{mgr.name}</h4>
                        <p style={styles.managerProject}>{mgr.project}</p>
                      </div>
                      <div style={styles.managerBadge}>
                        {mgr.openingsCount} Position{mgr.openingsCount > 1 ? 's' : ''}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>

          {/* REQUISITION DETAILS SECTION */}
          <div style={{ marginTop: '24px' }}>
            <h3 style={styles.sectionDividerTitle}>Hiring Requisitions Details</h3>
            <div style={styles.listContainer}>
              {Object.entries(groupedRequisitions).length === 0 ? (
                <EmptyState
                  icon="💼"
                  title="No Job Requisitions Open"
                  description="You do not have any job requisitions registered yet. Build new job descriptions with AI and publish openings to begin screening talent."
                  actionLabel="+ Create Job Opening"
                  onAction={() => navigate('/hr-dashboard/jd-builder')}
                />
              ) : (
                Object.entries(groupedRequisitions).map(([projectName, reqList]) => {
                const isExpanded = expandedProject === projectName;
                const projectTotalPositions = reqList.reduce((sum, r) => sum + (parseInt(r.count) || 1), 0);
                const managerName = reqList[0]?.manager_name || 'Assigned Manager';
                const rawBuName = reqList[0]?.bu_name || reqList[0]?.bu_head_name || '';
                const buName = (rawBuName === 'Assigned BU') ? '' : rawBuName;
                const publishedCount = reqList.filter(r => r.status === 'PUBLISHED').length;
                const pendingCount = reqList.filter(r => r.status === 'DRAFT' || r.status === 'BU_APPROVED').length;
                const filledCount = reqList.filter(r => r.status === 'FILLED').length;

                return (
                  <div key={projectName} style={styles.projectGroupCard}>
                    <div 
                      style={{
                        ...styles.projectHeader,
                        borderBottom: isExpanded ? '1px solid var(--divider-color, #e2e8f0)' : 'none',
                        paddingBottom: isExpanded ? '16px' : '0'
                      }}
                      onClick={() => toggleProject(projectName)}
                    >
                      <div style={styles.projectHeaderLeft}>
                        <div>
                          <h3 style={styles.projectTitle}>Project: {projectName}</h3>
                          <p style={styles.projectManagerSub}>Manager: {managerName}{buName ? ` | BU: ${buName}` : ''}</p>
                        </div>
                      </div>

                      <div style={styles.projectHeaderRight}>
                        <div style={styles.statsBadgesRow}>
                          <span style={styles.badgeTotal}>
                            {projectTotalPositions} Position{projectTotalPositions > 1 ? 's' : ''}
                          </span>
                          {publishedCount > 0 && (
                            <span style={styles.badgeActive}>
                              {publishedCount} Active
                            </span>
                          )}
                          {pendingCount > 0 && (
                            <span style={styles.badgePending}>
                              {pendingCount} Pending
                            </span>
                          )}
                          {filledCount > 0 && (
                            <span style={styles.badgeFilled}>
                              {filledCount} Filled
                            </span>
                          )}
                        </div>
                        <svg 
                          style={{
                            ...styles.chevronIcon,
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                          }} 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {isExpanded && (
                      <div style={styles.requisitionsGrid}>
                        {reqList.map((req) => {
                          const badgeStyles = getStatusStyle(req.status);
                          return (
                            <div key={req.id} style={styles.jobCard}>
                              <div style={styles.jobTop}>
                                <h4 style={styles.jobTitle}>{req.title}</h4>
                                <span style={{
                                  ...styles.statusBadge,
                                  background: badgeStyles.bg,
                                  color: badgeStyles.text,
                                  border: `1px solid ${badgeStyles.border}`
                                }}>
                                  {req.status}
                                </span>
                              </div>
                              
                              <div style={styles.jobDetails}>
                                <div style={styles.detailRow}>
                                  <span style={styles.detailLabel}>Skills:</span>
                                  <span style={styles.detailVal}>{req.skillset}</span>
                                </div>
                                <div style={styles.detailRow}>
                                  <span style={styles.detailLabel}>Experience:</span>
                                  <span style={styles.detailVal}>{req.exp_required}</span>
                                </div>
                                <div style={styles.detailRow}>
                                  <span style={styles.detailLabel}>Budget:</span>
                                  <span style={styles.detailVal}>₹{req.budget ? (parseInt(req.budget) / 100000).toFixed(1) : 0} LPA</span>
                                </div>
                                <div style={styles.detailRow}>
                                  <span style={styles.detailLabel}>Openings Count:</span>
                                  <span style={styles.detailVal}>{req.count} Positions</span>
                                </div>
                                <div style={styles.detailRow}>
                                  <span style={styles.detailLabel}>Hiring Manager:</span>
                                  <span style={styles.detailVal}>{req.manager_name}</span>
                                </div>
                              </div>
                              {req.status === 'BU_APPROVED' && (
                                <button
                                  onClick={() => handlePublish(req.id)}
                                  style={styles.publishBtn}
                                >
                                  Publish Position
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: '800',
    letterSpacing: '-0.5px'
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    color: 'var(--text-muted)'
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px'
  },
  metricCard: {
    background: 'var(--bg-card-glass)',
    backdropFilter: 'blur(16px)',
    border: '1px solid var(--border-light)',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: 'var(--shadow-sm)',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  metricLabel: {
    margin: 0,
    fontSize: '13px',
    fontWeight: '500',
    color: 'var(--text-muted)'
  },
  metricValue: {
    margin: 0,
    fontSize: '32px',
    fontWeight: '800',
    color: 'var(--text-primary)'
  },
  loader: {
    padding: '40px',
    textAlign: 'center',
    color: 'var(--text-muted)',
    fontSize: '14px'
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  projectGroupCard: {
    background: 'var(--bg-card-glass)',
    backdropFilter: 'blur(12px)',
    border: '1px solid var(--border-light)',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: 'var(--shadow-card)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  projectHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    cursor: 'pointer',
    userSelect: 'none'
  },
  projectHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  projectHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  },
  projectManagerSub: {
    margin: '4px 0 0 0',
    fontSize: '13px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '500'
  },
  statsBadgesRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },
  chevronIcon: {
    width: '20px',
    height: '20px',
    color: 'var(--text-muted, #64748b)',
    transition: 'transform 0.3s ease'
  },
  badgeTotal: {
    fontSize: '11px',
    fontWeight: '700',
    background: 'rgba(79, 70, 229, 0.1)',
    color: 'var(--accent-primary, #4f46e5)',
    padding: '4px 10px',
    borderRadius: '999px'
  },
  badgeActive: {
    fontSize: '11px',
    fontWeight: '700',
    background: 'rgba(16, 185, 129, 0.1)',
    color: 'var(--success-color, #10b981)',
    padding: '4px 10px',
    borderRadius: '999px'
  },
  badgePending: {
    fontSize: '11px',
    fontWeight: '700',
    background: 'rgba(245, 158, 11, 0.1)',
    color: 'var(--warning-color, #f59e0b)',
    padding: '4px 10px',
    borderRadius: '999px'
  },
  badgeFilled: {
    fontSize: '11px',
    fontWeight: '700',
    background: 'rgba(139, 92, 246, 0.1)',
    color: '#8b5cf6',
    padding: '4px 10px',
    borderRadius: '999px'
  },
  projectIcon: {
    width: '22px',
    height: '22px',
    color: 'var(--primary-color)'
  },
  projectTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--text-primary)'
  },
  headcountBadge: {
    fontSize: '11px',
    fontWeight: '600',
    background: 'var(--bg-hover)',
    padding: '3px 8px',
    borderRadius: '999px',
    color: 'var(--text-muted)'
  },
  requisitionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '16px'
  },
  jobCard: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    transition: 'transform 0.2s, box-shadow 0.2s',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: 'var(--shadow-md)'
    }
  },
  jobTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px'
  },
  jobTitle: {
    margin: 0,
    fontSize: '15px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    lineHeight: '1.4'
  },
  statusBadge: {
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.5px',
    padding: '3px 8px',
    borderRadius: '6px',
    whiteSpace: 'nowrap'
  },
  jobDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    lineHeight: '1.5'
  },
  detailLabel: {
    color: 'var(--text-muted)',
    fontWeight: '500'
  },
  detailVal: {
    color: 'var(--text-secondary)',
    fontWeight: '600',
    textAlign: 'right',
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  publishBtn: {
    marginTop: '12px',
    backgroundColor: '#10b981',
    color: '#ffffff',
    border: 'none',
    padding: '10px 16px',
    borderRadius: '10px',
    fontWeight: '700',
    fontSize: '13px',
    cursor: 'pointer',
    width: '100%',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 10px rgba(16, 185, 129, 0.2)',
  },
  dashboardSplitGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
    gap: '20px',
    marginTop: '8px'
  },
  dashboardCard: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  cardHeaderTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border-color, #e2e8f0)',
    paddingBottom: '12px'
  },
  managersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  managerItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
    borderBottom: '1px solid rgba(0, 0, 0, 0.02)'
  },
  managerAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '700'
  },
  managerInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  managerName: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-primary)'
  },
  managerProject: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--text-muted)'
  },
  managerBadge: {
    fontSize: '11px',
    fontWeight: '700',
    background: 'rgba(79, 70, 229, 0.08)',
    color: 'var(--primary-color)',
    padding: '4px 10px',
    borderRadius: '12px'
  },
  projectsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  projectItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  projectTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  projectName: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-primary)'
  },
  statusChip: {
    fontSize: '10px',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  projectProgressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  progressBarBg: {
    flex: 1,
    height: '6px',
    background: 'rgba(0, 0, 0, 0.05)',
    borderRadius: '3px',
    overflow: 'hidden'
  },
  progressBarFill: {
    height: '100%',
    borderRadius: '3px'
  },
  progressText: {
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    minWidth: '35px',
    textAlign: 'right'
  },
  sectionDividerTitle: {
    margin: '24px 0 12px 0',
    fontSize: '16px',
    fontWeight: '700',
    color: 'var(--text-primary)'
  }
};

export default RequisitionsMonitor;
